import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Select,
  Checkbox,
  Divider,
  useColorModeValue,
  useDisclosure,
  Flex,
  Grid,
  GridItem,
  useToast,
  Input,
  NumberInput,
  NumberInputField,
} from '@chakra-ui/react';
import { WarningIcon, CheckIcon } from '@chakra-ui/icons';

import type { VehicleCoverages, AccidentBenefits } from 'api/quote-marketplace';
import { apiCreateQuoteConsent } from 'api/quote-marketplace';
import {
  OPCF_20_LIMIT_PER_DAY_CAD,
  OPCF_27_LIMIT_CAD,
  OPCF_43_INDEMNITY_PERIOD_MONTHS,
} from '../constants/vehicleOptions';
import {
  LIABILITY_COVERAGE,
  ALL_PERILS_COVERAGE,
  COLLISION_COVERAGE,
  COMPREHENSIVE_COVERAGE,
  OPCF_20_COVERAGE,
  OPCF_27_COVERAGE,
  OPCF_43_COVERAGE,
  ACCIDENT_WAIVER_COVERAGE,
  getDeclinedRecommendedCoverages,
  getDeclinedOptionalAccidentBenefits,
  COVERAGE_ATTESTATION_TEXT,
} from '../constants/allCoveragesOptions';
import { useAnswerCodes } from '../hooks/useAnswerCodes';
import { useLiabilityLimitConfirmation } from '../hooks/useLiabilityLimitConfirmation';
import { useDcpdDeclineConfirmation } from '../hooks/useDcpdDeclineConfirmation';
import { usePhysicalDamageDeclineConfirmation } from '../hooks/usePhysicalDamageDeclineConfirmation';
import { useCoverageDeclineConfirmation } from '../hooks/useCoverageDeclineConfirmation';
import {
  isCoverageAvailable,
  hasAccidentBenefits,
  hasSef33Sef48,
  getEndorsementLabel,
  hasOpcf6aDescribedUse,
} from '../constants/provinceCoverageAvailability';
import {
  CONSENT_TYPE_ALL_COVERAGES_DECLINE,
  CONSENT_TYPE_OPCF_20_DECLINE,
  CONSENT_TYPE_OPCF_27_DECLINE,
  CONSENT_TYPE_OPCF_43_DECLINE,
  CONSENT_TYPE_OPCF_44_DECLINE,
  CONSENT_TYPE_ACCIDENT_WAIVER_DECLINE,
  CONSENT_TYPE_MINOR_CONVICTION_DECLINE,
} from '../constants';
import AccidentBenefitsModal from './AccidentBenefitsModal';
import DeclineAttestationModal from './DeclineAttestationModal';
import LiabilityLimitConfirmationModal from './LiabilityLimitConfirmationModal';
import DcpdDeclineConfirmationModal from './DcpdDeclineConfirmationModal';
import PhysicalDamageDeclineConfirmationModal from './PhysicalDamageDeclineConfirmationModal';
import CoverageConsentModal from './CoverageConsentModal';

const getRecommendedCoverageSnapshot = (
  coverages: VehicleCoverages,
  provinceCode: number,
): string => {
  const values = [
    coverages.liability_limit_cad || '',
    isCoverageAvailable('dcpd_deductible', provinceCode)
      ? coverages.dcpd_deductible_cad || ''
      : 'n/a',
    coverages.all_perils_enabled ? '1' : '0',
    coverages.collision_enabled ? '1' : '0',
    coverages.comprehensive_enabled ? '1' : '0',
    coverages.opcf_20_enabled ? '1' : '0',
    coverages.opcf_27_enabled ? '1' : '0',
    coverages.opcf_43_enabled ? '1' : '0',
    coverages.accident_waiver ? '1' : '0',
    coverages.opcf_44_enabled ? '1' : '0',
  ];

  if (hasAccidentBenefits(provinceCode) && coverages.accident_benefits) {
    const ab = coverages.accident_benefits;
    values.push(
      ab.income_replacement_enabled ? '1' : '0',
      ab.catastrophic_impairment_enabled ? '1' : '0',
      ab.non_catastrophic_impairment_enabled ? '1' : '0',
      ab.caregiver_enabled ? '1' : '0',
      ab.housekeeping_home_maintenance_enabled ? '1' : '0',
      ab.death_benefit_enabled ? '1' : '0',
      ab.funeral_benefit_enabled ? '1' : '0',
      ab.dependant_care_enabled ? '1' : '0',
      ab.non_earner_enabled ? '1' : '0',
      ab.lost_educational_expenses_enabled ? '1' : '0',
      ab.expenses_of_visitors_enabled ? '1' : '0',
      ab.damage_to_personal_items_enabled ? '1' : '0',
      ab.indexation_benefit_enabled ? '1' : '0',
    );
  }

  return values.join('|');
};

interface AllCoveragesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (coverages: VehicleCoverages, confirmed: boolean) => void;
  initialCoverages: VehicleCoverages;
  vehicleLabel: string;
  quoteId: number;
  vehicleVin: string;
  vehicleId: number;
  provinceCode: number;
}

const AllCoveragesModal = ({
  isOpen,
  onClose,
  onSave,
  initialCoverages,
  vehicleLabel,
  quoteId,
  vehicleVin,
  vehicleId,
  provinceCode,
}: AllCoveragesModalProps) => {
  const descriptionColor = useColorModeValue('gray.600', 'gray.400');
  const toast = useToast();
  const {
    getOptions,
    getLabel,
    isLoading: isLoadingCodes,
    isOtherDeductible,
  } = useAnswerCodes();

  const [coverages, setCoverages] =
    useState<VehicleCoverages>(initialCoverages);
  const [isConsentLoading, setIsConsentLoading] = useState(false);
  const initialCoverageSnapshotRef = useRef<string>('');

  const {
    isOpen: isAccidentBenefitsOpen,
    onOpen: onAccidentBenefitsOpen,
    onClose: onAccidentBenefitsClose,
  } = useDisclosure();

  const {
    isOpen: isAttestationOpen,
    onOpen: onAttestationOpen,
    onClose: onAttestationClose,
  } = useDisclosure();

  const handleApplyLiabilityChange = useCallback(
    (newCode: string, clearOther: boolean) => {
      setCoverages((prev) => ({
        ...prev,
        liability_limit_cad: newCode,
        liability_limit_other_cad: clearOther
          ? null
          : prev.liability_limit_other_cad,
      }));
    },
    [],
  );

  const liabilityConfirmation = useLiabilityLimitConfirmation({
    quoteId,
    vehicleVin,
    vehicleId,
    currentLiabilityCode: coverages.liability_limit_cad,
    getLabel,
    isOtherDeductible,
    onApplyChange: handleApplyLiabilityChange,
  });

  const handleApplyDcpdChange = useCallback(
    (newCode: string, clearOther: boolean) => {
      setCoverages((prev) => ({
        ...prev,
        dcpd_deductible_cad: newCode,
        dcpd_deductible_other_cad: clearOther
          ? null
          : prev.dcpd_deductible_other_cad,
      }));
    },
    [],
  );

  const dcpdConfirmation = useDcpdDeclineConfirmation({
    quoteId,
    vehicleVin,
    vehicleId,
    currentDcpdCode: coverages.dcpd_deductible_cad,
    isOtherDeductible,
    onApplyChange: handleApplyDcpdChange,
  });

  const clearAllPhysicalDamage = useCallback(() => {
    setCoverages((prev) => ({
      ...prev,
      collision_enabled: false,
      comprehensive_enabled: false,
      all_perils_enabled: false,
      collision_deductible_cad: '',
      collision_deductible_other_cad: null,
      comprehensive_deductible_cad: '',
      comprehensive_deductible_other_cad: null,
      all_perils_deductible_cad: '',
      all_perils_deductible_other_cad: null,
    }));
  }, []);

  const physicalDamageConfirmation = usePhysicalDamageDeclineConfirmation({
    quoteId,
    vehicleVin,
    vehicleId,
    onConfirmDecline: clearAllPhysicalDamage,
  });

  const handleApplyOpcf20Change = useCallback((enabled: boolean) => {
    setCoverages((prev) => ({
      ...prev,
      opcf_20_enabled: enabled,
      opcf_20_limit_per_day_cad: enabled
        ? prev.opcf_20_limit_per_day_cad
        : null,
    }));
  }, []);

  const { handleChange: handleOpcf20Change, modalProps: opcf20ModalProps } =
    useCoverageDeclineConfirmation({
      quoteId,
      vehicleVin,
      vehicleId,
      consentType: CONSENT_TYPE_OPCF_20_DECLINE,
      currentEnabled: coverages.opcf_20_enabled || false,
      onApplyChange: handleApplyOpcf20Change,
    });

  const handleApplyOpcf27Change = useCallback((enabled: boolean) => {
    setCoverages((prev) => ({
      ...prev,
      opcf_27_enabled: enabled,
      opcf_27_limit_cad: enabled ? prev.opcf_27_limit_cad : null,
    }));
  }, []);

  const { handleChange: handleOpcf27Change, modalProps: opcf27ModalProps } =
    useCoverageDeclineConfirmation({
      quoteId,
      vehicleVin,
      vehicleId,
      consentType: CONSENT_TYPE_OPCF_27_DECLINE,
      currentEnabled: coverages.opcf_27_enabled || false,
      onApplyChange: handleApplyOpcf27Change,
    });

  const handleApplyOpcf43Change = useCallback((enabled: boolean) => {
    setCoverages((prev) => ({
      ...prev,
      opcf_43_enabled: enabled,
      opcf_43_indemnity_period_months: enabled
        ? prev.opcf_43_indemnity_period_months
        : null,
    }));
  }, []);

  const { handleChange: handleOpcf43Change, modalProps: opcf43ModalProps } =
    useCoverageDeclineConfirmation({
      quoteId,
      vehicleVin,
      vehicleId,
      consentType: CONSENT_TYPE_OPCF_43_DECLINE,
      currentEnabled: coverages.opcf_43_enabled || false,
      onApplyChange: handleApplyOpcf43Change,
    });

  const handleApplyAccidentWaiverChange = useCallback((enabled: boolean) => {
    setCoverages((prev) => ({ ...prev, accident_waiver: enabled }));
  }, []);

  const {
    handleChange: handleAccidentWaiverChange,
    modalProps: accidentWaiverModalProps,
  } = useCoverageDeclineConfirmation({
    quoteId,
    vehicleVin,
    vehicleId,
    consentType: CONSENT_TYPE_ACCIDENT_WAIVER_DECLINE,
    currentEnabled: coverages.accident_waiver || false,
    onApplyChange: handleApplyAccidentWaiverChange,
  });

  const handleApplyMinorConvictionChange = useCallback((enabled: boolean) => {
    setCoverages((prev) => ({ ...prev, minor_conviction_protection: enabled }));
  }, []);

  const {
    handleChange: handleMinorConvictionChange,
    modalProps: minorConvictionModalProps,
  } = useCoverageDeclineConfirmation({
    quoteId,
    vehicleVin,
    vehicleId,
    consentType: CONSENT_TYPE_MINOR_CONVICTION_DECLINE,
    currentEnabled: coverages.minor_conviction_protection || false,
    onApplyChange: handleApplyMinorConvictionChange,
  });

  const handleApplyOpcf44Change = useCallback((enabled: boolean) => {
    setCoverages((prev) => ({ ...prev, opcf_44_enabled: enabled }));
  }, []);

  const { handleChange: handleOpcf44Change, modalProps: opcf44ModalProps } =
    useCoverageDeclineConfirmation({
      quoteId,
      vehicleVin,
      vehicleId,
      consentType: CONSENT_TYPE_OPCF_44_DECLINE,
      currentEnabled: coverages.opcf_44_enabled || false,
      onApplyChange: handleApplyOpcf44Change,
    });

  const handleCollisionToggle = useCallback(
    (checked: boolean) => {
      if (checked) {
        setCoverages((prev) => ({
          ...prev,
          collision_enabled: true,
          all_perils_enabled: false,
          all_perils_deductible_cad: '',
          all_perils_deductible_other_cad: null,
        }));
      } else {
        const willHaveNoCoverage =
          !coverages.comprehensive_enabled && !coverages.all_perils_enabled;
        if (willHaveNoCoverage) {
          physicalDamageConfirmation.triggerConfirmation();
        } else {
          setCoverages((prev) => ({
            ...prev,
            collision_enabled: false,
            collision_deductible_cad: '',
            collision_deductible_other_cad: null,
          }));
        }
      }
    },
    [coverages, physicalDamageConfirmation],
  );

  const handleComprehensiveToggle = useCallback(
    (checked: boolean) => {
      if (checked) {
        setCoverages((prev) => ({
          ...prev,
          comprehensive_enabled: true,
          all_perils_enabled: false,
          all_perils_deductible_cad: '',
          all_perils_deductible_other_cad: null,
        }));
      } else {
        const willHaveNoCoverage =
          !coverages.collision_enabled && !coverages.all_perils_enabled;
        if (willHaveNoCoverage) {
          physicalDamageConfirmation.triggerConfirmation();
        } else {
          setCoverages((prev) => ({
            ...prev,
            comprehensive_enabled: false,
            comprehensive_deductible_cad: '',
            comprehensive_deductible_other_cad: null,
          }));
        }
      }
    },
    [coverages, physicalDamageConfirmation],
  );

  const handleAllPerilsToggle = useCallback(
    (checked: boolean) => {
      if (checked) {
        setCoverages((prev) => ({
          ...prev,
          all_perils_enabled: true,
          collision_enabled: false,
          comprehensive_enabled: false,
          collision_deductible_cad: '',
          collision_deductible_other_cad: null,
          comprehensive_deductible_cad: '',
          comprehensive_deductible_other_cad: null,
        }));
      } else {
        physicalDamageConfirmation.triggerConfirmation();
      }
    },
    [physicalDamageConfirmation],
  );

  useEffect(() => {
    if (isOpen) {
      setCoverages(initialCoverages);
      liabilityConfirmation.resetState();
      dcpdConfirmation.resetState();
      physicalDamageConfirmation.resetState();
      initialCoverageSnapshotRef.current = getRecommendedCoverageSnapshot(
        initialCoverages,
        provinceCode,
      );
    }
  }, [
    isOpen,
    initialCoverages,
    liabilityConfirmation.resetState,
    dcpdConfirmation.resetState,
    physicalDamageConfirmation.resetState,
    provinceCode,
  ]);

  const updateCoverage = useCallback(
    <K extends keyof VehicleCoverages>(key: K, value: VehicleCoverages[K]) => {
      setCoverages((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleAccidentBenefitsSave = useCallback(
    (benefits: AccidentBenefits, confirmed: boolean) => {
      setCoverages((prev) => ({
        ...prev,
        accident_benefits: benefits,
        accident_benefits_confirmed: confirmed,
      }));
    },
    [],
  );

  const handleApply = useCallback(() => {
    const declinedCoverages = getDeclinedRecommendedCoverages(
      coverages,
      provinceCode,
      getLabel,
    );
    const declinedBenefits = hasAccidentBenefits(provinceCode)
      ? getDeclinedOptionalAccidentBenefits(coverages.accident_benefits)
      : [];

    const hasDeclines =
      declinedCoverages.length > 0 || declinedBenefits.length > 0;

    const currentSnapshot = getRecommendedCoverageSnapshot(
      coverages,
      provinceCode,
    );
    const coveragesChanged =
      currentSnapshot !== initialCoverageSnapshotRef.current;
    const wasConfirmed = initialCoverages.all_coverages_confirmed || false;

    const shouldShowAttestation =
      hasDeclines && (!wasConfirmed || coveragesChanged);

    if (shouldShowAttestation) {
      onAttestationOpen();
    } else {
      onSave(coverages, true);
      onClose();
    }
  }, [
    coverages,
    onSave,
    onClose,
    onAttestationOpen,
    provinceCode,
    getLabel,
    initialCoverages.all_coverages_confirmed,
  ]);

  const handleAttestationConfirm = useCallback(
    async (consentMethod: number) => {
      setIsConsentLoading(true);
      try {
        await apiCreateQuoteConsent(quoteId, {
          type: CONSENT_TYPE_ALL_COVERAGES_DECLINE,
          method: consentMethod,
          vehicle_vin: vehicleVin || undefined,
          vehicle_id: !vehicleVin ? vehicleId : undefined,
        });
        onAttestationClose();
        onSave(coverages, true);
        onClose();
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to record consent. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsConsentLoading(false);
      }
    },
    [
      quoteId,
      vehicleVin,
      vehicleId,
      coverages,
      onSave,
      onClose,
      onAttestationClose,
      toast,
    ],
  );

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const accidentBenefitsConfirmed =
    coverages.accident_benefits_confirmed || false;
  const declinedCoverages = getDeclinedRecommendedCoverages(
    coverages,
    provinceCode,
    getLabel,
  );
  const declinedBenefits = hasAccidentBenefits(provinceCode)
    ? getDeclinedOptionalAccidentBenefits(coverages.accident_benefits)
    : [];

  const renderDescription = (description: string) => (
    <Text
      fontSize="sm"
      color={descriptionColor}
      pl={3}
      borderLeft="2px solid"
      borderColor="orange.400"
    >
      {description}
    </Text>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCancel}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader fontWeight="600" pb={2}>
            <Flex justify="space-between" align="center" pr={8}>
              <Text>All Coverages</Text>
              {hasAccidentBenefits(provinceCode) && (
                <Button
                  size="sm"
                  bg={accidentBenefitsConfirmed ? 'blue.700' : 'red.700'}
                  color="white"
                  _hover={{
                    bg: accidentBenefitsConfirmed ? 'blue.800' : 'red.800',
                  }}
                  leftIcon={
                    accidentBenefitsConfirmed ? <CheckIcon /> : <WarningIcon />
                  }
                  onClick={onAccidentBenefitsOpen}
                >
                  Accident Benefits
                </Button>
              )}
            </Flex>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* Liability Section */}
              <Box>
                <Text fontWeight="700" mb={2}>
                  Liability
                  <Text as="span" color="red.500" ml={1}>
                    *
                  </Text>
                </Text>
                <Grid templateColumns="1fr 1fr" gap={6}>
                  <GridItem>
                    {renderDescription(LIABILITY_COVERAGE.description)}
                  </GridItem>
                  <GridItem>
                    <VStack spacing={2} align="stretch">
                      <Box>
                        <Text fontSize="sm" mb={1}>
                          Liability / Property Damage
                        </Text>
                        <HStack spacing={2}>
                          <Select
                            variant="main"
                            size="sm"
                            height={30}
                            value={coverages.liability_limit_cad || ''}
                            onChange={(e) =>
                              liabilityConfirmation.handleLiabilityLimitChange(
                                e.target.value,
                              )
                            }
                            isDisabled={isLoadingCodes}
                            isInvalid={!coverages.liability_limit_cad}
                          >
                            {getOptions('liability_limit_cad').map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </Select>
                          {isOtherDeductible(
                            coverages.liability_limit_cad || '',
                            'liability_limit_cad',
                          ) && (
                            <NumberInput
                              variant="main"
                              size="sm"
                              w="100px"
                              min={0}
                              value={coverages.liability_limit_other_cad ?? ''}
                              onChange={(valueString) =>
                                updateCoverage(
                                  'liability_limit_other_cad',
                                  valueString ? Number(valueString) : null,
                                )
                              }
                              isInvalid={!coverages.liability_limit_other_cad}
                            >
                              <NumberInputField
                                placeholder="Amount"
                                height={30}
                                borderColor={
                                  !coverages.liability_limit_other_cad
                                    ? 'red.600'
                                    : undefined
                                }
                              />
                            </NumberInput>
                          )}
                        </HStack>
                      </Box>
                      {isCoverageAvailable('dcpd_deductible', provinceCode) && (
                        <Box>
                          <Text fontSize="sm" mb={1}>
                            Direct Compensation
                          </Text>
                          <HStack spacing={2}>
                            <Select
                              variant="main"
                              size="sm"
                              height={30}
                              value={coverages.dcpd_deductible_cad || ''}
                              onChange={(e) =>
                                dcpdConfirmation.handleDcpdChange(
                                  e.target.value,
                                )
                              }
                              isDisabled={isLoadingCodes}
                            >
                              {getOptions('dcpd_deductible_cad').map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.value === ''
                                    ? 'No DCPD Coverage'
                                    : opt.label}
                                </option>
                              ))}
                            </Select>
                            {isOtherDeductible(
                              coverages.dcpd_deductible_cad || '',
                              'dcpd_deductible_cad',
                            ) && (
                              <NumberInput
                                variant="main"
                                size="sm"
                                w="100px"
                                min={0}
                                value={
                                  coverages.dcpd_deductible_other_cad ?? ''
                                }
                                onChange={(valueString) =>
                                  updateCoverage(
                                    'dcpd_deductible_other_cad',
                                    valueString ? Number(valueString) : null,
                                  )
                                }
                                isInvalid={!coverages.dcpd_deductible_other_cad}
                              >
                                <NumberInputField
                                  placeholder="BASE"
                                  height={30}
                                  borderColor={
                                    !coverages.dcpd_deductible_other_cad
                                      ? 'red.600'
                                      : undefined
                                  }
                                />
                              </NumberInput>
                            )}
                          </HStack>
                        </Box>
                      )}
                    </VStack>
                  </GridItem>
                </Grid>
              </Box>

              <Divider />

              {/* Physical Damage Coverage Section */}
              <Box>
                <Text fontWeight="700" mb={3}>
                  Physical Damage Coverage
                </Text>

                <VStack align="stretch" spacing={3}>
                  {/* Collision */}
                  <HStack spacing={4} align="flex-start">
                    <Checkbox
                      isChecked={coverages.collision_enabled || false}
                      onChange={(e) => handleCollisionToggle(e.target.checked)}
                      opacity={coverages.all_perils_enabled ? 0.5 : 1}
                      mt={1}
                    />
                    <VStack align="stretch" spacing={1} flex="1">
                      <HStack justify="space-between">
                        <Text
                          fontSize="sm"
                          color={
                            coverages.all_perils_enabled
                              ? 'gray.400'
                              : 'inherit'
                          }
                        >
                          Collision
                        </Text>
                        <HStack spacing={2}>
                          <Select
                            variant="main"
                            size="sm"
                            w="140px"
                            height={30}
                            value={coverages.collision_deductible_cad || ''}
                            onChange={(e) => {
                              updateCoverage(
                                'collision_deductible_cad',
                                e.target.value,
                              );
                              if (
                                !isOtherDeductible(
                                  e.target.value,
                                  'collision_deductible_cad',
                                )
                              ) {
                                updateCoverage(
                                  'collision_deductible_other_cad',
                                  null,
                                );
                              }
                            }}
                            isDisabled={
                              isLoadingCodes ||
                              !coverages.collision_enabled ||
                              coverages.all_perils_enabled
                            }
                            opacity={
                              !coverages.collision_enabled ||
                              coverages.all_perils_enabled
                                ? 0.5
                                : 1
                            }
                            placeholder={
                              coverages.collision_enabled &&
                              !coverages.all_perils_enabled
                                ? 'Select'
                                : ''
                            }
                          >
                            {getOptions('collision_deductible_cad').map(
                              (opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.value === '' ? 'Select' : opt.label}
                                </option>
                              ),
                            )}
                          </Select>
                          {isOtherDeductible(
                            coverages.collision_deductible_cad || '',
                            'collision_deductible_cad',
                          ) && (
                            <NumberInput
                              variant="main"
                              size="sm"
                              w="100px"
                              min={0}
                              value={
                                coverages.collision_deductible_other_cad ?? ''
                              }
                              onChange={(valueString) =>
                                updateCoverage(
                                  'collision_deductible_other_cad',
                                  valueString ? Number(valueString) : null,
                                )
                              }
                              isDisabled={
                                !coverages.collision_enabled ||
                                coverages.all_perils_enabled
                              }
                              opacity={
                                !coverages.collision_enabled ||
                                coverages.all_perils_enabled
                                  ? 0.5
                                  : 1
                              }
                            >
                              <NumberInputField
                                placeholder="BASE"
                                height={30}
                              />
                            </NumberInput>
                          )}
                        </HStack>
                      </HStack>
                      {renderDescription(COLLISION_COVERAGE.description)}
                    </VStack>
                  </HStack>

                  {/* Comprehensive */}
                  <HStack spacing={4} align="flex-start">
                    <Checkbox
                      isChecked={coverages.comprehensive_enabled || false}
                      onChange={(e) =>
                        handleComprehensiveToggle(e.target.checked)
                      }
                      opacity={coverages.all_perils_enabled ? 0.5 : 1}
                      mt={1}
                    />
                    <VStack align="stretch" spacing={1} flex="1">
                      <HStack justify="space-between">
                        <Text
                          fontSize="sm"
                          color={
                            coverages.all_perils_enabled
                              ? 'gray.400'
                              : 'inherit'
                          }
                        >
                          Comprehensive
                        </Text>
                        <HStack spacing={2}>
                          <Select
                            variant="main"
                            size="sm"
                            w="140px"
                            height={30}
                            value={coverages.comprehensive_deductible_cad || ''}
                            onChange={(e) => {
                              updateCoverage(
                                'comprehensive_deductible_cad',
                                e.target.value,
                              );
                              if (
                                !isOtherDeductible(
                                  e.target.value,
                                  'comprehensive_deductible_cad',
                                )
                              ) {
                                updateCoverage(
                                  'comprehensive_deductible_other_cad',
                                  null,
                                );
                              }
                            }}
                            isDisabled={
                              isLoadingCodes ||
                              !coverages.comprehensive_enabled ||
                              coverages.all_perils_enabled
                            }
                            opacity={
                              !coverages.comprehensive_enabled ||
                              coverages.all_perils_enabled
                                ? 0.5
                                : 1
                            }
                            placeholder={
                              coverages.comprehensive_enabled &&
                              !coverages.all_perils_enabled
                                ? 'Select'
                                : ''
                            }
                          >
                            {getOptions('comprehensive_deductible_cad').map(
                              (opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.value === '' ? 'Select' : opt.label}
                                </option>
                              ),
                            )}
                          </Select>
                          {isOtherDeductible(
                            coverages.comprehensive_deductible_cad || '',
                            'comprehensive_deductible_cad',
                          ) && (
                            <NumberInput
                              variant="main"
                              size="sm"
                              w="100px"
                              min={0}
                              value={
                                coverages.comprehensive_deductible_other_cad ??
                                ''
                              }
                              onChange={(valueString) =>
                                updateCoverage(
                                  'comprehensive_deductible_other_cad',
                                  valueString ? Number(valueString) : null,
                                )
                              }
                              isDisabled={
                                !coverages.comprehensive_enabled ||
                                coverages.all_perils_enabled
                              }
                              opacity={
                                !coverages.comprehensive_enabled ||
                                coverages.all_perils_enabled
                                  ? 0.5
                                  : 1
                              }
                            >
                              <NumberInputField
                                placeholder="BASE"
                                height={30}
                              />
                            </NumberInput>
                          )}
                        </HStack>
                      </HStack>
                      {renderDescription(COMPREHENSIVE_COVERAGE.description)}
                    </VStack>
                  </HStack>

                  {/* All Perils */}
                  <HStack spacing={4} align="flex-start">
                    <Checkbox
                      isChecked={coverages.all_perils_enabled || false}
                      onChange={(e) => handleAllPerilsToggle(e.target.checked)}
                      opacity={
                        coverages.collision_enabled ||
                        coverages.comprehensive_enabled
                          ? 0.5
                          : 1
                      }
                      mt={1}
                    />
                    <VStack align="stretch" spacing={1} flex="1">
                      <HStack justify="space-between">
                        <Text
                          fontSize="sm"
                          color={
                            coverages.collision_enabled ||
                            coverages.comprehensive_enabled
                              ? 'gray.400'
                              : 'inherit'
                          }
                        >
                          All Perils
                        </Text>
                        <HStack spacing={2}>
                          <Select
                            variant="main"
                            size="sm"
                            w="140px"
                            height={30}
                            value={coverages.all_perils_deductible_cad || ''}
                            onChange={(e) => {
                              updateCoverage(
                                'all_perils_deductible_cad',
                                e.target.value,
                              );
                              if (
                                !isOtherDeductible(
                                  e.target.value,
                                  'all_perils_deductible_cad',
                                )
                              ) {
                                updateCoverage(
                                  'all_perils_deductible_other_cad',
                                  null,
                                );
                              }
                            }}
                            isDisabled={
                              isLoadingCodes || !coverages.all_perils_enabled
                            }
                            opacity={!coverages.all_perils_enabled ? 0.5 : 1}
                            placeholder={
                              coverages.all_perils_enabled ? 'Select' : ''
                            }
                          >
                            {getOptions('all_perils_deductible_cad').map(
                              (opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.value === '' ? 'Select' : opt.label}
                                </option>
                              ),
                            )}
                          </Select>
                          {isOtherDeductible(
                            coverages.all_perils_deductible_cad || '',
                            'all_perils_deductible_cad',
                          ) && (
                            <NumberInput
                              variant="main"
                              size="sm"
                              w="100px"
                              min={0}
                              value={
                                coverages.all_perils_deductible_other_cad ?? ''
                              }
                              onChange={(valueString) =>
                                updateCoverage(
                                  'all_perils_deductible_other_cad',
                                  valueString ? Number(valueString) : null,
                                )
                              }
                              isDisabled={!coverages.all_perils_enabled}
                              opacity={!coverages.all_perils_enabled ? 0.5 : 1}
                            >
                              <NumberInputField
                                placeholder="BASE"
                                height={30}
                              />
                            </NumberInput>
                          )}
                        </HStack>
                      </HStack>
                      {renderDescription(ALL_PERILS_COVERAGE.description)}
                    </VStack>
                  </HStack>
                </VStack>
              </Box>

              <Divider />

              {/* Endorsements Section */}
              <Box>
                <Text fontWeight="700" mb={3}>
                  Endorsements
                </Text>

                <VStack align="stretch" spacing={0} divider={<Divider />}>
                  {/* #2 Drive Other Automobiles */}
                  {isCoverageAvailable('opcf_2', provinceCode) && (
                    <Box py={3}>
                      <Checkbox
                        isChecked={coverages.opcf_2_enabled || false}
                        onChange={(e) =>
                          updateCoverage('opcf_2_enabled', e.target.checked)
                        }
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">#2 Drive Other Automobiles</Text>
                      </Checkbox>
                    </Box>
                  )}

                  {isCoverageAvailable('opcf_6a_passengers', provinceCode) && (
                    <Box py={3}>
                      <Checkbox
                        isChecked={
                          coverages.opcf_6a_passengers_enabled || false
                        }
                        onChange={(e) =>
                          updateCoverage(
                            'opcf_6a_passengers_enabled',
                            e.target.checked,
                          )
                        }
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">
                          {getEndorsementLabel(
                            'opcf_6a_passengers',
                            provinceCode,
                            '#6a Permission to Carry Passengers',
                          )}
                        </Text>
                      </Checkbox>
                      {coverages.opcf_6a_passengers_enabled &&
                        hasOpcf6aDescribedUse(provinceCode) && (
                          <Input
                            variant="main"
                            size="sm"
                            mt={2}
                            ml={6}
                            w="calc(100% - 24px)"
                            height={30}
                            value={coverages.opcf_6a_described_use || ''}
                            onChange={(e) =>
                              updateCoverage(
                                'opcf_6a_described_use',
                                e.target.value,
                              )
                            }
                            placeholder="Describe use (e.g., taxi, livery)"
                          />
                        )}
                    </Box>
                  )}

                  {isCoverageAvailable('opcf_8', provinceCode) && (
                    <Box py={3}>
                      <HStack justify="space-between" mb={1}>
                        <Checkbox
                          isChecked={coverages.opcf_8_enabled || false}
                          onChange={(e) =>
                            updateCoverage('opcf_8_enabled', e.target.checked)
                          }
                          colorScheme="blue"
                        >
                          <Text fontSize="sm">
                            {getEndorsementLabel(
                              'opcf_8',
                              provinceCode,
                              '#8 Property Damage Reimbursement',
                            )}
                          </Text>
                        </Checkbox>

                        <NumberInput
                          variant="main"
                          size="sm"
                          w="120px"
                          min={0}
                          value={
                            coverages.opcf_8_limit_cad != null
                              ? String(coverages.opcf_8_limit_cad)
                              : ''
                          }
                          onChange={(valueString) =>
                            updateCoverage(
                              'opcf_8_limit_cad',
                              valueString ? Number(valueString) : null,
                            )
                          }
                          isDisabled={!coverages.opcf_8_enabled}
                        >
                          <NumberInputField
                            placeholder={coverages.opcf_8_enabled ? 'BASE' : ''}
                          />
                        </NumberInput>
                      </HStack>
                    </Box>
                  )}

                  {isCoverageAvailable('opcf_8a', provinceCode) && (
                    <Box py={3}>
                      <HStack justify="space-between" mb={1}>
                        <Checkbox
                          isChecked={coverages.opcf_8a_enabled || false}
                          onChange={(e) =>
                            updateCoverage('opcf_8a_enabled', e.target.checked)
                          }
                          colorScheme="blue"
                        >
                          <Text fontSize="sm">
                            {getEndorsementLabel(
                              'opcf_8a',
                              provinceCode,
                              '#8a Named Person Property Damage Reimbursement',
                            )}
                          </Text>
                        </Checkbox>
                        <HStack spacing={2}>
                          <Box>
                            <Text fontSize="xs" color="gray.500" mb={1}>
                              Limit
                            </Text>
                            <NumberInput
                              variant="main"
                              size="sm"
                              w="100px"
                              min={0}
                              value={
                                coverages.opcf_8a_limit_cad != null
                                  ? String(coverages.opcf_8a_limit_cad)
                                  : ''
                              }
                              onChange={(valueString) =>
                                updateCoverage(
                                  'opcf_8a_limit_cad',
                                  valueString ? Number(valueString) : null,
                                )
                              }
                              isDisabled={!coverages.opcf_8a_enabled}
                            >
                              <NumberInputField
                                placeholder={
                                  coverages.opcf_8a_enabled ? 'BASE' : ''
                                }
                              />
                            </NumberInput>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500" mb={1}>
                              Deductible
                            </Text>
                            <NumberInput
                              variant="main"
                              size="sm"
                              w="100px"
                              min={0}
                              value={
                                coverages.opcf_8a_deductible_cad != null
                                  ? String(coverages.opcf_8a_deductible_cad)
                                  : ''
                              }
                              onChange={(valueString) =>
                                updateCoverage(
                                  'opcf_8a_deductible_cad',
                                  valueString ? Number(valueString) : null,
                                )
                              }
                              isDisabled={!coverages.opcf_8a_enabled}
                            >
                              <NumberInputField
                                placeholder={
                                  coverages.opcf_8a_enabled ? 'BASE' : ''
                                }
                              />
                            </NumberInput>
                          </Box>
                        </HStack>
                      </HStack>
                    </Box>
                  )}

                  {/* #4min Transportation Network */}
                  {isCoverageAvailable('opcf_4_min', provinceCode) && (
                    <Box py={3}>
                      <Checkbox
                        isChecked={coverages.opcf_4_min_enabled || false}
                        onChange={(e) =>
                          updateCoverage('opcf_4_min_enabled', e.target.checked)
                        }
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">#4min Transportation Network</Text>
                      </Checkbox>
                    </Box>
                  )}

                  {/* #19a Agreed Value */}
                  <Box py={3}>
                    <HStack justify="space-between" mb={1}>
                      <Checkbox
                        isChecked={coverages.opcf_19a_enabled || false}
                        onChange={(e) =>
                          updateCoverage('opcf_19a_enabled', e.target.checked)
                        }
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">#19a Agreed Value</Text>
                      </Checkbox>
                      <NumberInput
                        variant="main"
                        size="sm"
                        w="120px"
                        min={0}
                        value={
                          coverages.opcf_19a_insured_amount_cad != null
                            ? String(coverages.opcf_19a_insured_amount_cad)
                            : ''
                        }
                        onChange={(valueString) =>
                          updateCoverage(
                            'opcf_19a_insured_amount_cad',
                            valueString ? Number(valueString) : null,
                          )
                        }
                        isDisabled={!coverages.opcf_19a_enabled}
                      >
                        <NumberInputField
                          placeholder={coverages.opcf_19a_enabled ? 'BASE' : ''}
                        />
                      </NumberInput>
                    </HStack>
                  </Box>

                  {/* #20 Loss of Use */}
                  <Box py={3}>
                    <HStack justify="space-between" mb={1}>
                      <Checkbox
                        isChecked={coverages.opcf_20_enabled || false}
                        onChange={(e) => handleOpcf20Change(e.target.checked)}
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">#20 Loss of Use</Text>
                      </Checkbox>
                      <Select
                        variant="main"
                        size="sm"
                        w="120px"
                        height={30}
                        value={coverages.opcf_20_limit_per_day_cad ?? ''}
                        onChange={(e) =>
                          updateCoverage(
                            'opcf_20_limit_per_day_cad',
                            e.target.value ? Number(e.target.value) : null,
                          )
                        }
                        isDisabled={!coverages.opcf_20_enabled}
                      >
                        <option value="">
                          {coverages.opcf_20_enabled ? 'Select' : ''}
                        </option>
                        {OPCF_20_LIMIT_PER_DAY_CAD.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Select>
                    </HStack>
                    <Box pl={6}>
                      {renderDescription(OPCF_20_COVERAGE.description)}
                    </Box>
                  </Box>

                  {isCoverageAvailable(
                    'opcf_20_disappearing',
                    provinceCode,
                  ) && (
                    <Box py={3}>
                      <Checkbox
                        isChecked={
                          coverages.opcf_20_disappearing_deductible || false
                        }
                        onChange={(e) =>
                          updateCoverage(
                            'opcf_20_disappearing_deductible',
                            e.target.checked,
                          )
                        }
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">
                          {getEndorsementLabel(
                            'opcf_20_disappearing',
                            provinceCode,
                            '#26 Disappearing Deductible',
                          )}
                        </Text>
                      </Checkbox>
                    </Box>
                  )}

                  {/* #27 Liability for Damage */}
                  <Box py={3}>
                    <HStack justify="space-between" mb={1}>
                      <Checkbox
                        isChecked={coverages.opcf_27_enabled || false}
                        onChange={(e) => handleOpcf27Change(e.target.checked)}
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">#27 Liability for Damage</Text>
                      </Checkbox>
                      <Select
                        variant="main"
                        size="sm"
                        w="120px"
                        height={30}
                        value={coverages.opcf_27_limit_cad ?? ''}
                        onChange={(e) =>
                          updateCoverage(
                            'opcf_27_limit_cad',
                            e.target.value ? Number(e.target.value) : null,
                          )
                        }
                        isDisabled={!coverages.opcf_27_enabled}
                      >
                        <option value="">
                          {coverages.opcf_27_enabled ? 'Select' : ''}
                        </option>
                        {OPCF_27_LIMIT_CAD.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Select>
                    </HStack>
                    <Box pl={6}>
                      {renderDescription(OPCF_27_COVERAGE.description)}
                    </Box>
                  </Box>

                  {/* #35 Emergency Roadside Service */}
                  <Box py={3}>
                    <Checkbox
                      isChecked={coverages.opcf_35_enabled || false}
                      onChange={(e) =>
                        updateCoverage('opcf_35_enabled', e.target.checked)
                      }
                      colorScheme="blue"
                    >
                      <Text fontSize="sm">#35 Emergency Roadside Service</Text>
                    </Checkbox>
                  </Box>

                  {/* #43 Limited Waiver */}
                  <Box py={3}>
                    <HStack justify="space-between" mb={1}>
                      <Checkbox
                        isChecked={coverages.opcf_43_enabled || false}
                        onChange={(e) => handleOpcf43Change(e.target.checked)}
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">#43 Limited Waiver</Text>
                      </Checkbox>
                      <Select
                        variant="main"
                        size="sm"
                        w="120px"
                        height={30}
                        value={coverages.opcf_43_indemnity_period_months ?? ''}
                        onChange={(e) =>
                          updateCoverage(
                            'opcf_43_indemnity_period_months',
                            e.target.value ? Number(e.target.value) : null,
                          )
                        }
                        isDisabled={!coverages.opcf_43_enabled}
                      >
                        <option value="">
                          {coverages.opcf_43_enabled ? 'Select' : ''}
                        </option>
                        {OPCF_43_INDEMNITY_PERIOD_MONTHS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Select>
                    </HStack>
                    <Box pl={6}>
                      {renderDescription(OPCF_43_COVERAGE.description)}
                    </Box>
                  </Box>

                  {/* #44 Family Protection */}
                  <Box py={3}>
                    <HStack justify="space-between">
                      <Checkbox
                        isChecked={coverages.opcf_44_enabled || false}
                        onChange={(e) => handleOpcf44Change(e.target.checked)}
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">#44 Family Protection</Text>
                      </Checkbox>
                      {coverages.opcf_44_enabled && (
                        <Text fontSize="sm" color="gray.500">
                          Matches Liability
                        </Text>
                      )}
                    </HStack>
                  </Box>

                  {/* #48 Added Coverage to Offset Tort Deductibles */}
                  {isCoverageAvailable('opcf_48', provinceCode) && (
                    <Box py={3}>
                      <Checkbox
                        isChecked={coverages.opcf_48_enabled || false}
                        onChange={(e) =>
                          updateCoverage('opcf_48_enabled', e.target.checked)
                        }
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">
                          #48 Added Coverage to Offset Tort Deductibles
                        </Text>
                      </Checkbox>
                    </Box>
                  )}

                  {/* #49 Agreement Not to Recover */}
                  {isCoverageAvailable('opcf_49', provinceCode) && (
                    <Box py={3}>
                      <Checkbox
                        isChecked={coverages.opcf_49_enabled || false}
                        onChange={(e) =>
                          updateCoverage('opcf_49_enabled', e.target.checked)
                        }
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">
                          #49 Agreement Not to Recover for Loss or Damage from
                          an Automobile Collision
                        </Text>
                      </Checkbox>
                    </Box>
                  )}

                  {/* Waiver of deductible (claim specific) */}
                  <Box py={3}>
                    <Checkbox
                      isChecked={coverages.waiver_of_deductible || false}
                      onChange={(e) =>
                        updateCoverage('waiver_of_deductible', e.target.checked)
                      }
                      colorScheme="blue"
                    >
                      <Text fontSize="sm">
                        Waiver of deductible (claim specific)
                      </Text>
                    </Checkbox>
                  </Box>

                  {/* Accident Waiver/Forgiveness */}
                  <Box py={3}>
                    <Checkbox
                      isChecked={coverages.accident_waiver || false}
                      onChange={(e) =>
                        handleAccidentWaiverChange(e.target.checked)
                      }
                      colorScheme="blue"
                      mb={1}
                    >
                      <Text fontSize="sm">Accident Waiver/Forgiveness</Text>
                    </Checkbox>
                    <Box pl={6}>
                      {renderDescription(ACCIDENT_WAIVER_COVERAGE.description)}
                    </Box>
                  </Box>

                  {/* Permission to rent or lease the vehicle for carsharing */}
                  <Box py={3}>
                    <Checkbox
                      isChecked={coverages.carsharing_enabled || false}
                      onChange={(e) =>
                        updateCoverage('carsharing_enabled', e.target.checked)
                      }
                      colorScheme="blue"
                    >
                      <Text fontSize="sm">
                        Permission to rent or lease the vehicle for carsharing
                      </Text>
                    </Checkbox>
                  </Box>

                  {/* Minor Conviction Protection */}
                  <Box py={3}>
                    <Checkbox
                      isChecked={coverages.minor_conviction_protection || false}
                      onChange={(e) =>
                        handleMinorConvictionChange(e.target.checked)
                      }
                      colorScheme="blue"
                    >
                      <Text fontSize="sm">Minor Conviction Protection</Text>
                    </Checkbox>
                  </Box>

                  {/* SEF33/SEF48 - Nova Scotia and Alberta only */}
                  {hasSef33Sef48(provinceCode) && (
                    <>
                      <Box py={3}>
                        <Checkbox
                          isChecked={coverages.sef33_enabled || false}
                          onChange={(e) =>
                            updateCoverage('sef33_enabled', e.target.checked)
                          }
                          colorScheme="blue"
                        >
                          <Text fontSize="sm">
                            SEF 33 - Accident Benefits (Pedestrian Coverage)
                          </Text>
                        </Checkbox>
                      </Box>
                      <Box py={3}>
                        <Checkbox
                          isChecked={coverages.sef48_enabled || false}
                          onChange={(e) =>
                            updateCoverage('sef48_enabled', e.target.checked)
                          }
                          colorScheme="blue"
                        >
                          <Text fontSize="sm">
                            SEF 48 - Optional Supplementary Benefits
                          </Text>
                        </Checkbox>
                      </Box>
                    </>
                  )}

                  {isCoverageAvailable('sef_13c', provinceCode) && (
                    <Box py={3}>
                      <Checkbox
                        isChecked={coverages.sef_13c_enabled || false}
                        onChange={(e) =>
                          updateCoverage('sef_13c_enabled', e.target.checked)
                        }
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">
                          #13c Comprehensive Cover Deletion of Glass
                        </Text>
                      </Checkbox>
                    </Box>
                  )}

                  {isCoverageAvailable('sef_13d', provinceCode) && (
                    <Box py={3}>
                      <Checkbox
                        isChecked={coverages.sef_13d_enabled || false}
                        onChange={(e) =>
                          updateCoverage('sef_13d_enabled', e.target.checked)
                        }
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">
                          #13d Limitation of Glass Coverage
                        </Text>
                      </Checkbox>
                    </Box>
                  )}

                  {isCoverageAvailable('sef_49a', provinceCode) && (
                    <Box py={3}>
                      <Checkbox
                        isChecked={coverages.sef_49a_enabled || false}
                        onChange={(e) =>
                          updateCoverage('sef_49a_enabled', e.target.checked)
                        }
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">
                          #49a DCPD Deletion Endorsement
                        </Text>
                      </Checkbox>
                    </Box>
                  )}

                  {isCoverageAvailable('cae_17', provinceCode) && (
                    <Box py={3}>
                      <Checkbox
                        isChecked={coverages.cae_17_enabled || false}
                        onChange={(e) =>
                          updateCoverage('cae_17_enabled', e.target.checked)
                        }
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">
                          CAE17 Hail Deductible Endorsement
                        </Text>
                      </Checkbox>
                    </Box>
                  )}

                  {isCoverageAvailable('cae_18', provinceCode) && (
                    <Box py={3}>
                      <Checkbox
                        isChecked={coverages.cae_18_enabled || false}
                        onChange={(e) =>
                          updateCoverage('cae_18_enabled', e.target.checked)
                        }
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">
                          CAE18 Deletion of Hail Endorsement
                        </Text>
                      </Checkbox>
                    </Box>
                  )}

                  {isCoverageAvailable('sef_13h', provinceCode) && (
                    <Box py={3}>
                      <Checkbox
                        isChecked={coverages.sef_13h_enabled || false}
                        onChange={(e) =>
                          updateCoverage('sef_13h_enabled', e.target.checked)
                        }
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">
                          #13h Existing Hail Damage – Deletion of Hail Coverage
                        </Text>
                      </Checkbox>
                    </Box>
                  )}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter gap={2} pt={4}>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button colorScheme="yellow" onClick={handleApply}>
              Apply
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {hasAccidentBenefits(provinceCode) && (
        <AccidentBenefitsModal
          isOpen={isAccidentBenefitsOpen}
          onClose={onAccidentBenefitsClose}
          onSave={handleAccidentBenefitsSave}
          initialBenefits={coverages.accident_benefits}
          initialConfirmed={coverages.accident_benefits_confirmed}
          vehicleLabel={vehicleLabel}
          quoteId={quoteId}
          vehicleVin={vehicleVin}
          vehicleId={vehicleId}
        />
      )}

      <DeclineAttestationModal
        isOpen={isAttestationOpen}
        onClose={onAttestationClose}
        onConfirm={handleAttestationConfirm}
        declinedItems={declinedCoverages.map((c, i) => ({
          key: `coverage-${i}`,
          name: c.name,
        }))}
        attestationText={COVERAGE_ATTESTATION_TEXT}
        secondaryItems={declinedBenefits.map((b, i) => ({
          key: `benefit-${i}`,
          name: b,
        }))}
        secondaryTitle="Declined Accident Benefits:"
        isLoading={isConsentLoading}
        size="lg"
      />

      <LiabilityLimitConfirmationModal {...liabilityConfirmation.modalProps} />
      <DcpdDeclineConfirmationModal {...dcpdConfirmation.modalProps} />
      <PhysicalDamageDeclineConfirmationModal
        {...physicalDamageConfirmation.modalProps}
      />
      <CoverageConsentModal {...opcf20ModalProps} />
      <CoverageConsentModal {...opcf27ModalProps} />
      <CoverageConsentModal {...opcf43ModalProps} />
      <CoverageConsentModal {...accidentWaiverModalProps} />
      <CoverageConsentModal {...minorConvictionModalProps} />
      <CoverageConsentModal {...opcf44ModalProps} />
    </>
  );
};

export default AllCoveragesModal;
